(() => {
  const CONSENT_CLEANUP_KEY = '__qDigitalConsentCleanup';
  const CONSENT_PENDING_CLASS = 'q-product-form--digital-consent-pending';
  const defer = typeof queueMicrotask === 'function'
    ? queueMicrotask
    : (callback) => Promise.resolve().then(callback);

  const createController = ({
    form,
    select,
    submit,
    variants,
    isDigital,
    digitalConsent,
  }) => {
    const gate = window.QProductFormState?.forForm(form);
    const panel = document.createElement('div');
    panel.className = 'q-digital-consent';
    panel.setAttribute('data-generated-digital-consent', '');

    const label = document.createElement('label');
    label.className = 'q-digital-consent__label';
    const checkbox = document.createElement('input');
    checkbox.className = 'q-digital-consent__checkbox';
    checkbox.type = 'checkbox';
    checkbox.name = `properties[${digitalConsent.propertyName}]`;
    checkbox.value = digitalConsent.propertyValue;
    const text = document.createElement('span');
    text.className = 'q-digital-consent__text';
    text.textContent = digitalConsent.text;
    label.append(checkbox, text);
    panel.append(label);
    submit.before(panel);

    let active = true;
    let selectedVariantId = null;

    const currentVariant = () => variants.find(
      (variant) => variant.id === String(select.value),
    ) || null;
    const isAvailable = (variant) => {
      const nativeAvailability = select.selectedOptions?.[0]?.dataset?.variantAvailable;
      return nativeAvailability === undefined
        ? Boolean(variant?.available)
        : nativeAvailability !== 'false';
    };
    const setSubmitDisabled = (disabled) => {
      if (submit.getAttribute('aria-busy') === 'true' && !disabled) return;
      if (submit.disabled !== disabled) submit.disabled = disabled;

      const ariaDisabled = String(disabled);
      if (submit.getAttribute('aria-disabled') !== ariaDisabled) {
        submit.setAttribute('aria-disabled', ariaDisabled);
      }
    };
    const synchronize = () => {
      if (!active) return;

      const variant = currentVariant();
      const nextVariantId = variant?.id || '';
      if (selectedVariantId !== null && nextVariantId !== selectedVariantId) {
        checkbox.checked = false;
      }
      selectedVariantId = nextVariantId;

      const digital = isDigital(variant);
      if (!digital) checkbox.checked = false;

      panel.hidden = !digital;
      checkbox.disabled = !digital;
      checkbox.required = digital;

      const pending = digital && !checkbox.checked;
      form.classList.toggle(CONSENT_PENDING_CLASS, pending);
      if (gate) {
        gate.setAvailability(isAvailable(variant));
        gate.setBlocker('digital-consent', pending ? digitalConsent.text : null);
      } else setSubmitDisabled(!isAvailable(variant) || pending);
    };
    const submitObserver = !gate && typeof MutationObserver === 'function'
      ? new MutationObserver(() => synchronize())
      : null;
    submitObserver?.observe(submit, {
      attributes: true,
      attributeFilter: ['aria-busy', 'disabled'],
    });
    const queueSynchronization = () => defer(synchronize);
    const onConsentChange = () => synchronize();
    const onSubmit = (event) => {
      synchronize();
      if (!isDigital(currentVariant()) || checkbox.checked) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      checkbox.focus();
      checkbox.reportValidity();
    };
    const cleanup = () => {
      if (!active) return;
      active = false;
      checkbox.removeEventListener('change', onConsentChange);
      form.removeEventListener('submit', onSubmit, true);
      submitObserver?.disconnect();
      panel.remove();
      form.classList.remove(CONSENT_PENDING_CLASS);
      const variant = currentVariant();
      if (gate) gate.setBlocker('digital-consent', null);
      else setSubmitDisabled(!isAvailable(variant));
      if (form[CONSENT_CLEANUP_KEY] === cleanup) delete form[CONSENT_CLEANUP_KEY];
    };

    checkbox.addEventListener('change', onConsentChange);
    form.addEventListener('submit', onSubmit, true);
    form[CONSENT_CLEANUP_KEY] = cleanup;

    return { synchronize, queueSynchronization, cleanup };
  };

  window.QProductDigitalConsent = { createController };
})();
